export const AI_CONTRACT_VERSION = '6.0.4';

export const UIActionType = Object.freeze({
  NAVIGATE_TARGET: 'ui.navigate.target',
  NAVIGATE_ROUTE: 'ui.navigate.route',
  NAVIGATE_SCREEN: 'ui.navigate.screen',
  PORTFOLIO_SET_DATE_RANGE: 'ui.portfolio.setDateRange',
  PORTFOLIO_FILTER_PERIOD: 'ui.portfolio.filterPeriod',
  PORTFOLIO_SET_FILTERS: 'ui.portfolio.setFilters',
  PORTFOLIO_FILTER_SSIDS: 'ui.portfolio.filterSsids',
  PORTFOLIO_FILTER_EXTERNAL_REPORTING: 'ui.portfolio.filterExternalReporting',
  PORTFOLIO_FILTER_TAX_CLASS: 'ui.portfolio.filterTaxClass',
  PORTFOLIO_FILTER_INVESTMENT_ACCOUNT: 'ui.portfolio.filterInvestmentAccount',
  PORTFOLIO_ACTIVE_STRATEGY: 'ui.portfolio.activeStrategy',
  FEED_FILTER: 'ui.feed.filter',
  DOCUMENTS_FILTER_RELATIONSHIPS: 'ui.documents.filterDocumentRelationships',
  DOCUMENTS_FILTER_YEARS: 'ui.documents.filterDocumentYears',
  DOCUMENTS_FILTER_TYPES: 'ui.documents.filterDocumentTypes',
  DOCUMENTS_RESET_FILTERS: 'ui.documents.resetFilters',
  DOCUMENTS_SHOW_ONLY_UNREAD: 'ui.documents.showOnlyUnread',
  USER_SET_LANGUAGE: 'ui.user.setLanguage',
});

export const DataToolType = Object.freeze({
  PORTFOLIO_TOP_PERFORMING_PRODUCTS: 'data.portfolio.topPerformingProducts',
  PORTFOLIO_QUERY: 'data.portfolio.query',
  PORTFOLIO_TRANSACTIONS_QUERY: 'data.portfolio.transactions.query',
  PORTFOLIO_ACCESSIBLE_ACCOUNTS: 'data.portfolio.accessibleAccounts',
  PORTFOLIO_MARKETVALUES_QUERY: 'data.portfolio.marketvalues.query',
  PORTFOLIO_SECURITIES_QUERY: 'data.portfolio.securities.query',
  PORTFOLIO_FUNDS_QUERY: 'data.portfolio.funds.query',
  PORTFOLIO_COST_REPORT_QUERY: 'data.portfolio.costReport.query',
  PORTFOLIO_STRATEGY_QUERY: 'data.portfolio.strategy.query',
  PORTFOLIO_TAX_DETAILS_QUERY: 'data.portfolio.taxDetails.query',
  WEALTH_PLAN_QUERY: 'data.wealthPlan.query',
});

export const ACTION_ERROR_CODES = Object.freeze({
  UNKNOWN_ACTION: 'UNKNOWN_ACTION',
  INVALID_ACTION: 'INVALID_ACTION',
  INVALID_PAYLOAD: 'INVALID_PAYLOAD',
  NAVIGATION_FAILED: 'NAVIGATION_FAILED',
  FILTER_NOT_AVAILABLE: 'FILTER_NOT_AVAILABLE',
  CHAT_SERVER_UNREACHABLE: 'CHAT_SERVER_UNREACHABLE',
  CHAT_RESPONSE_INVALID: 'CHAT_RESPONSE_INVALID',
});

export const SUPPORTED_LANGUAGE_CODES = Object.freeze(['nb', 'sv', 'da', 'en']);
export const PORTFOLIO_PERFORMANCE_METRICS = Object.freeze(['twr', 'gain']);
export const PORTFOLIO_SUPPORTED_TOOL_PERIODS = Object.freeze([
  'THISMONTH',
  'THISYEAR',
  'TWELVEMONTHS',
  'THREEYEARS',
  'FIVEYEARS',
  'SINCESTART',
  'EIGHTYEARS',
  'TENYEARS',
  'CUSTOM',
]);

const createObjectPayloadSchema = ({
  properties = {},
  required = [],
  additionalProperties = false,
}) =>
  Object.freeze({
    type: 'object',
    properties: Object.freeze(properties),
    required: Object.freeze(required),
    additionalProperties,
  });

export const UI_ACTION_PAYLOAD_SCHEMAS = Object.freeze({
  [UIActionType.NAVIGATE_TARGET]: createObjectPayloadSchema({
    properties: {
      targetId: { type: 'string' },
      params: { type: 'object' },
      search: { type: 'string' },
      hash: { type: 'string' },
    },
    required: ['targetId'],
  }),
  [UIActionType.NAVIGATE_ROUTE]: createObjectPayloadSchema({
    properties: {
      path: { type: 'string' },
      search: { type: 'string' },
      hash: { type: 'string' },
    },
    required: ['path'],
  }),
  [UIActionType.NAVIGATE_SCREEN]: createObjectPayloadSchema({
    properties: {
      name: { type: 'string' },
      params: { type: 'object' },
    },
    required: ['name'],
  }),
  [UIActionType.PORTFOLIO_SET_DATE_RANGE]: createObjectPayloadSchema({
    properties: {
      from: { type: 'string' },
      to: { type: 'string' },
    },
    required: ['from', 'to'],
  }),
  [UIActionType.PORTFOLIO_FILTER_PERIOD]: createObjectPayloadSchema({
    properties: {
      period: { type: 'string' },
      startDate: { type: 'string' },
      endDate: { type: 'string' },
    },
    required: ['period'],
  }),
  [UIActionType.PORTFOLIO_SET_FILTERS]: createObjectPayloadSchema({
    additionalProperties: true,
  }),
  [UIActionType.PORTFOLIO_FILTER_SSIDS]: createObjectPayloadSchema({
    properties: {
      ssids: {
        type: 'array',
        items: {
          oneOf: [{ type: 'string' }, { type: 'number' }],
        },
      },
    },
    required: ['ssids'],
  }),
  [UIActionType.PORTFOLIO_FILTER_EXTERNAL_REPORTING]: createObjectPayloadSchema(
    {
      properties: {
        value: { type: 'string' },
      },
      required: ['value'],
    }
  ),
  [UIActionType.PORTFOLIO_FILTER_TAX_CLASS]: createObjectPayloadSchema({
    properties: {
      value: { type: 'string' },
    },
    required: ['value'],
  }),
  [UIActionType.PORTFOLIO_FILTER_INVESTMENT_ACCOUNT]: createObjectPayloadSchema(
    {
      properties: {
        value: {
          oneOf: [
            { type: 'string' },
            {
              type: 'array',
              items: { type: 'string' },
            },
          ],
        },
      },
      required: ['value'],
    }
  ),
  [UIActionType.PORTFOLIO_ACTIVE_STRATEGY]: createObjectPayloadSchema({
    properties: {
      strategyId: {
        oneOf: [{ type: 'string' }, { type: 'number' }, { type: 'null' }],
      },
    },
    required: ['strategyId'],
  }),
  [UIActionType.FEED_FILTER]: createObjectPayloadSchema({
    properties: {
      type: { type: 'string' },
    },
    required: ['type'],
  }),
  [UIActionType.DOCUMENTS_FILTER_RELATIONSHIPS]: createObjectPayloadSchema({
    properties: {
      relationships: {
        type: 'array',
        items: {
          oneOf: [{ type: 'string' }, { type: 'number' }],
        },
      },
    },
    required: ['relationships'],
  }),
  [UIActionType.DOCUMENTS_FILTER_YEARS]: createObjectPayloadSchema({
    properties: {
      year: {
        oneOf: [{ type: 'string' }, { type: 'number' }],
      },
    },
    required: ['year'],
  }),
  [UIActionType.DOCUMENTS_FILTER_TYPES]: createObjectPayloadSchema({
    properties: {
      documentTypes: {
        type: 'array',
        items: { type: 'string' },
      },
    },
    required: ['documentTypes'],
  }),
  [UIActionType.DOCUMENTS_RESET_FILTERS]: createObjectPayloadSchema({}),
  [UIActionType.DOCUMENTS_SHOW_ONLY_UNREAD]: createObjectPayloadSchema({
    properties: {
      value: { type: 'boolean' },
    },
    required: ['value'],
  }),
  [UIActionType.USER_SET_LANGUAGE]: createObjectPayloadSchema({
    properties: {
      language: {
        type: 'string',
        enum: SUPPORTED_LANGUAGE_CODES,
      },
    },
    required: ['language'],
  }),
});

export const DATA_TOOL_PAYLOAD_SCHEMAS = Object.freeze({
  [DataToolType.PORTFOLIO_TOP_PERFORMING_PRODUCTS]: createObjectPayloadSchema({
    properties: {
      metric: {
        type: 'string',
        enum: PORTFOLIO_PERFORMANCE_METRICS,
      },
      topN: {
        type: 'integer',
        minimum: 1,
        maximum: 20,
      },
      period: {
        type: 'string',
        enum: PORTFOLIO_SUPPORTED_TOOL_PERIODS,
      },
      from: { type: 'string' },
      to: { type: 'string' },
      ssids: {
        type: 'array',
        items: {
          oneOf: [{ type: 'string' }, { type: 'number' }],
        },
      },
      minMarketValue: { type: 'number' },
      includeSold: { type: 'boolean' },
    },
  }),
  [DataToolType.PORTFOLIO_QUERY]: createObjectPayloadSchema({
    properties: {
      question: { type: 'string' },
      dataScopes: {
        type: 'array',
        items: { type: 'string' },
      },
      period: {
        type: 'string',
        enum: PORTFOLIO_SUPPORTED_TOOL_PERIODS,
      },
      from: { type: 'string' },
      to: { type: 'string' },
      ssids: {
        type: 'array',
        items: {
          oneOf: [{ type: 'string' }, { type: 'number' }],
        },
      },
      includeSold: { type: 'boolean' },
      maxRows: { type: 'integer', minimum: 1, maximum: 2000 },
    },
    required: ['question'],
  }),
  [DataToolType.PORTFOLIO_TRANSACTIONS_QUERY]: createObjectPayloadSchema({
    properties: {
      question: { type: 'string' },
      period: {
        type: 'string',
        enum: PORTFOLIO_SUPPORTED_TOOL_PERIODS,
      },
      from: { type: 'string' },
      to: { type: 'string' },
      ssids: {
        type: 'array',
        items: {
          oneOf: [{ type: 'string' }, { type: 'number' }],
        },
      },
      transactionTypes: {
        type: 'array',
        items: { type: 'string' },
      },
      maxRows: { type: 'integer', minimum: 1, maximum: 2000 },
      sort: {
        type: 'string',
        enum: ['date_desc', 'date_asc', 'amount_desc', 'amount_asc'],
      },
    },
    required: ['question'],
  }),
  [DataToolType.PORTFOLIO_ACCESSIBLE_ACCOUNTS]: createObjectPayloadSchema({}),
  [DataToolType.PORTFOLIO_MARKETVALUES_QUERY]: createObjectPayloadSchema({
    properties: {
      question: { type: 'string' },
      period: {
        type: 'string',
        enum: PORTFOLIO_SUPPORTED_TOOL_PERIODS,
      },
      from: { type: 'string' },
      to: { type: 'string' },
      ssids: {
        type: 'array',
        items: {
          oneOf: [{ type: 'string' }, { type: 'number' }],
        },
      },
      granularity: {
        type: 'string',
        enum: ['MONTH', 'YEAR'],
      },
      maxRows: { type: 'integer', minimum: 1, maximum: 2000 },
    },
    required: ['question'],
  }),
  [DataToolType.PORTFOLIO_SECURITIES_QUERY]: createObjectPayloadSchema({
    properties: {
      question: { type: 'string' },
      period: {
        type: 'string',
        enum: PORTFOLIO_SUPPORTED_TOOL_PERIODS,
      },
      from: { type: 'string' },
      to: { type: 'string' },
      ssids: {
        type: 'array',
        items: {
          oneOf: [{ type: 'string' }, { type: 'number' }],
        },
      },
      maxRows: { type: 'integer', minimum: 1, maximum: 2000 },
    },
    required: ['question'],
  }),
  [DataToolType.PORTFOLIO_FUNDS_QUERY]: createObjectPayloadSchema({
    properties: {
      question: { type: 'string' },
      period: {
        type: 'string',
        enum: PORTFOLIO_SUPPORTED_TOOL_PERIODS,
      },
      from: { type: 'string' },
      to: { type: 'string' },
      ssids: {
        type: 'array',
        items: {
          oneOf: [{ type: 'string' }, { type: 'number' }],
        },
      },
      maxRows: { type: 'integer', minimum: 1, maximum: 2000 },
    },
    required: ['question'],
  }),
  [DataToolType.PORTFOLIO_COST_REPORT_QUERY]: createObjectPayloadSchema({
    properties: {
      question: { type: 'string' },
      year: { type: 'integer', minimum: 1900, maximum: 2200 },
      ssids: {
        type: 'array',
        items: {
          oneOf: [{ type: 'string' }, { type: 'number' }],
        },
      },
      maxRows: { type: 'integer', minimum: 1, maximum: 2000 },
    },
    required: ['question'],
  }),
  [DataToolType.PORTFOLIO_STRATEGY_QUERY]: createObjectPayloadSchema({
    properties: {
      question: { type: 'string' },
      period: {
        type: 'string',
        enum: PORTFOLIO_SUPPORTED_TOOL_PERIODS,
      },
      from: { type: 'string' },
      to: { type: 'string' },
      ssids: {
        type: 'array',
        items: {
          oneOf: [{ type: 'string' }, { type: 'number' }],
        },
      },
      maxRows: { type: 'integer', minimum: 1, maximum: 2000 },
    },
    required: ['question'],
  }),
  [DataToolType.PORTFOLIO_TAX_DETAILS_QUERY]: createObjectPayloadSchema({
    properties: {
      question: { type: 'string' },
      year: { type: 'integer', minimum: 1900, maximum: 2200 },
      ssids: {
        type: 'array',
        items: {
          oneOf: [{ type: 'string' }, { type: 'number' }],
        },
      },
      maxRows: { type: 'integer', minimum: 1, maximum: 2000 },
    },
    required: ['question'],
  }),
  [DataToolType.WEALTH_PLAN_QUERY]: createObjectPayloadSchema({
    properties: {
      question: { type: 'string' },
      dataScopes: {
        type: 'array',
        items: { type: 'string' },
      },
      expertInputId: { type: 'string' },
      maxRows: { type: 'integer', minimum: 1, maximum: 500 },
    },
    required: ['question'],
  }),
});

export const UI_ACTION_METADATA = Object.freeze({
  [UIActionType.NAVIGATE_TARGET]: {
    id: UIActionType.NAVIGATE_TARGET,
    description:
      'Navigate by stable target id resolved through the shared navigation catalog.',
    payloadSchema: UI_ACTION_PAYLOAD_SCHEMAS[UIActionType.NAVIGATE_TARGET],
    examples: [{ targetId: 'portfolio.transactions' }],
  },
  [UIActionType.NAVIGATE_ROUTE]: {
    id: UIActionType.NAVIGATE_ROUTE,
    description:
      'Navigate to a route path. Generic route action intended for web clients.',
    payloadSchema: UI_ACTION_PAYLOAD_SCHEMAS[UIActionType.NAVIGATE_ROUTE],
    examples: [{ path: '/portfolio/transactions' }],
  },
  [UIActionType.NAVIGATE_SCREEN]: {
    id: UIActionType.NAVIGATE_SCREEN,
    description:
      'Navigate by React Navigation screen name. Generic action intended for mobile clients.',
    payloadSchema: UI_ACTION_PAYLOAD_SCHEMAS[UIActionType.NAVIGATE_SCREEN],
    examples: [{ name: 'Transactions' }],
  },
  [UIActionType.PORTFOLIO_SET_DATE_RANGE]: {
    id: UIActionType.PORTFOLIO_SET_DATE_RANGE,
    description: 'Applies a custom portfolio date range (from/to).',
    payloadSchema:
      UI_ACTION_PAYLOAD_SCHEMAS[UIActionType.PORTFOLIO_SET_DATE_RANGE],
    examples: [{ from: '2025-01-01', to: '2025-12-31' }],
  },
  [UIActionType.PORTFOLIO_FILTER_PERIOD]: {
    id: UIActionType.PORTFOLIO_FILTER_PERIOD,
    description: 'Dispatches ui.portfolio.filterPeriod.',
    payloadSchema:
      UI_ACTION_PAYLOAD_SCHEMAS[UIActionType.PORTFOLIO_FILTER_PERIOD],
    examples: [{ period: 'THISYEAR' }],
  },
  [UIActionType.PORTFOLIO_SET_FILTERS]: {
    id: UIActionType.PORTFOLIO_SET_FILTERS,
    description: 'Dispatches ui.portfolio.setFilters.',
    payloadSchema:
      UI_ACTION_PAYLOAD_SCHEMAS[UIActionType.PORTFOLIO_SET_FILTERS],
    examples: [{ accountIds: ['1234'] }],
  },
  [UIActionType.PORTFOLIO_FILTER_SSIDS]: {
    id: UIActionType.PORTFOLIO_FILTER_SSIDS,
    description: 'Dispatches ui.portfolio.filterSsids.',
    payloadSchema:
      UI_ACTION_PAYLOAD_SCHEMAS[UIActionType.PORTFOLIO_FILTER_SSIDS],
    examples: [{ ssids: ['123', '456'] }],
  },
  [UIActionType.PORTFOLIO_FILTER_EXTERNAL_REPORTING]: {
    id: UIActionType.PORTFOLIO_FILTER_EXTERNAL_REPORTING,
    description: 'Dispatches ui.portfolio.filterExternalReporting.',
    payloadSchema:
      UI_ACTION_PAYLOAD_SCHEMAS[
        UIActionType.PORTFOLIO_FILTER_EXTERNAL_REPORTING
      ],
    examples: [{ value: 'exclude' }],
  },
  [UIActionType.PORTFOLIO_FILTER_TAX_CLASS]: {
    id: UIActionType.PORTFOLIO_FILTER_TAX_CLASS,
    description: 'Dispatches ui.portfolio.filterTaxClass.',
    payloadSchema:
      UI_ACTION_PAYLOAD_SCHEMAS[UIActionType.PORTFOLIO_FILTER_TAX_CLASS],
    examples: [{ value: 'Tax Free' }],
  },
  [UIActionType.PORTFOLIO_FILTER_INVESTMENT_ACCOUNT]: {
    id: UIActionType.PORTFOLIO_FILTER_INVESTMENT_ACCOUNT,
    description: 'Dispatches ui.portfolio.filterInvestmentAccount.',
    payloadSchema:
      UI_ACTION_PAYLOAD_SCHEMAS[
        UIActionType.PORTFOLIO_FILTER_INVESTMENT_ACCOUNT
      ],
    examples: [{ value: ['AF123', 'AF456'] }],
  },
  [UIActionType.PORTFOLIO_ACTIVE_STRATEGY]: {
    id: UIActionType.PORTFOLIO_ACTIVE_STRATEGY,
    description: 'Dispatches ui.portfolio.activeStrategy.',
    payloadSchema:
      UI_ACTION_PAYLOAD_SCHEMAS[UIActionType.PORTFOLIO_ACTIVE_STRATEGY],
    examples: [{ strategyId: 'balanced' }],
  },
  [UIActionType.FEED_FILTER]: {
    id: UIActionType.FEED_FILTER,
    description: 'Dispatches ui.feed.filter.',
    payloadSchema: UI_ACTION_PAYLOAD_SCHEMAS[UIActionType.FEED_FILTER],
    examples: [{ type: 'news' }],
  },
  [UIActionType.DOCUMENTS_FILTER_RELATIONSHIPS]: {
    id: UIActionType.DOCUMENTS_FILTER_RELATIONSHIPS,
    description: 'Dispatches ui.documents.filterDocumentRelationships.',
    payloadSchema:
      UI_ACTION_PAYLOAD_SCHEMAS[UIActionType.DOCUMENTS_FILTER_RELATIONSHIPS],
    examples: [{ relationships: ['owner-1'] }],
  },
  [UIActionType.DOCUMENTS_FILTER_YEARS]: {
    id: UIActionType.DOCUMENTS_FILTER_YEARS,
    description: 'Dispatches ui.documents.filterDocumentYears.',
    payloadSchema:
      UI_ACTION_PAYLOAD_SCHEMAS[UIActionType.DOCUMENTS_FILTER_YEARS],
    examples: [{ year: 2025 }],
  },
  [UIActionType.DOCUMENTS_FILTER_TYPES]: {
    id: UIActionType.DOCUMENTS_FILTER_TYPES,
    description: 'Dispatches ui.documents.filterDocumentTypes.',
    payloadSchema:
      UI_ACTION_PAYLOAD_SCHEMAS[UIActionType.DOCUMENTS_FILTER_TYPES],
    examples: [{ documentTypes: ['tax'] }],
  },
  [UIActionType.DOCUMENTS_RESET_FILTERS]: {
    id: UIActionType.DOCUMENTS_RESET_FILTERS,
    description: 'Dispatches ui.documents.resetFilters.',
    payloadSchema:
      UI_ACTION_PAYLOAD_SCHEMAS[UIActionType.DOCUMENTS_RESET_FILTERS],
    examples: [{}],
  },
  [UIActionType.DOCUMENTS_SHOW_ONLY_UNREAD]: {
    id: UIActionType.DOCUMENTS_SHOW_ONLY_UNREAD,
    description: 'Dispatches ui.documents.showOnlyUnread.',
    payloadSchema:
      UI_ACTION_PAYLOAD_SCHEMAS[UIActionType.DOCUMENTS_SHOW_ONLY_UNREAD],
    examples: [{ value: true }],
  },
  [UIActionType.USER_SET_LANGUAGE]: {
    id: UIActionType.USER_SET_LANGUAGE,
    description: 'Dispatches ui.user.setLanguage.',
    payloadSchema: UI_ACTION_PAYLOAD_SCHEMAS[UIActionType.USER_SET_LANGUAGE],
    examples: [{ language: 'nb' }],
  },
});

export const DATA_TOOL_METADATA = Object.freeze({
  [DataToolType.PORTFOLIO_TOP_PERFORMING_PRODUCTS]: {
    id: DataToolType.PORTFOLIO_TOP_PERFORMING_PRODUCTS,
    description:
      'Returns the highest performing products from portfolio data based on twr or gain.',
    payloadSchema:
      DATA_TOOL_PAYLOAD_SCHEMAS[DataToolType.PORTFOLIO_TOP_PERFORMING_PRODUCTS],
    examples: [{ metric: 'twr', topN: 5, period: 'THISYEAR' }],
  },
  [DataToolType.PORTFOLIO_QUERY]: {
    id: DataToolType.PORTFOLIO_QUERY,
    description:
      'Fetches portfolio data for broad LLM reasoning when no specialized tool fits.',
    payloadSchema: DATA_TOOL_PAYLOAD_SCHEMAS[DataToolType.PORTFOLIO_QUERY],
    examples: [{ question: 'Summarize portfolio performance this year.' }],
  },
  [DataToolType.PORTFOLIO_TRANSACTIONS_QUERY]: {
    id: DataToolType.PORTFOLIO_TRANSACTIONS_QUERY,
    description:
      'Fetches transaction history and summary data for natural-language transaction analysis.',
    payloadSchema:
      DATA_TOOL_PAYLOAD_SCHEMAS[DataToolType.PORTFOLIO_TRANSACTIONS_QUERY],
    examples: [{ question: 'List the largest buys in 2025.', maxRows: 20 }],
  },
  [DataToolType.PORTFOLIO_ACCESSIBLE_ACCOUNTS]: {
    id: DataToolType.PORTFOLIO_ACCESSIBLE_ACCOUNTS,
    description:
      'Returns the currently accessible portfolio accounts for mapping account names to SSIDs in portfolio filtering.',
    payloadSchema:
      DATA_TOOL_PAYLOAD_SCHEMAS[DataToolType.PORTFOLIO_ACCESSIBLE_ACCOUNTS],
    examples: [{}],
  },
  [DataToolType.PORTFOLIO_MARKETVALUES_QUERY]: {
    id: DataToolType.PORTFOLIO_MARKETVALUES_QUERY,
    description:
      'Returns market value time series (including gain, twr, deposits/withdrawals, and per-asset-class breakdowns).',
    payloadSchema:
      DATA_TOOL_PAYLOAD_SCHEMAS[DataToolType.PORTFOLIO_MARKETVALUES_QUERY],
    examples: [{ question: 'Show market value development this year.' }],
  },
  [DataToolType.PORTFOLIO_SECURITIES_QUERY]: {
    id: DataToolType.PORTFOLIO_SECURITIES_QUERY,
    description:
      'Returns owned securities and fund relationships, including direct/indirect ownership and market-value composition.',
    payloadSchema:
      DATA_TOOL_PAYLOAD_SCHEMAS[DataToolType.PORTFOLIO_SECURITIES_QUERY],
    examples: [{ question: 'List my largest securities.', maxRows: 25 }],
  },
  [DataToolType.PORTFOLIO_FUNDS_QUERY]: {
    id: DataToolType.PORTFOLIO_FUNDS_QUERY,
    description:
      'Returns fund and fund-of-funds holdings with classification, index references, ownership links, and market values.',
    payloadSchema:
      DATA_TOOL_PAYLOAD_SCHEMAS[DataToolType.PORTFOLIO_FUNDS_QUERY],
    examples: [{ question: 'Show my directly owned funds.' }],
  },
  [DataToolType.PORTFOLIO_COST_REPORT_QUERY]: {
    id: DataToolType.PORTFOLIO_COST_REPORT_QUERY,
    description:
      'Returns annual client cost-report rows (fees, supplier costs, rates) and aggregated cost totals.',
    payloadSchema:
      DATA_TOOL_PAYLOAD_SCHEMAS[DataToolType.PORTFOLIO_COST_REPORT_QUERY],
    examples: [{ question: 'Show my cost report for 2024.', year: 2024 }],
  },
  [DataToolType.PORTFOLIO_STRATEGY_QUERY]: {
    id: DataToolType.PORTFOLIO_STRATEGY_QUERY,
    description:
      'Returns Strategy-page data: strategy groups, selected-group allocation vs targets/ranges, and related illiquid cashflow/securities context.',
    payloadSchema:
      DATA_TOOL_PAYLOAD_SCHEMAS[DataToolType.PORTFOLIO_STRATEGY_QUERY],
    examples: [{ question: 'How far am I from target strategy?' }],
  },
  [DataToolType.PORTFOLIO_TAX_DETAILS_QUERY]: {
    id: DataToolType.PORTFOLIO_TAX_DETAILS_QUERY,
    description:
      'Returns annual tax-detail rows for deductible fees and taxable income for the selected account.',
    payloadSchema:
      DATA_TOOL_PAYLOAD_SCHEMAS[DataToolType.PORTFOLIO_TAX_DETAILS_QUERY],
    examples: [{ question: 'Show tax details for last year.' }],
  },
  [DataToolType.WEALTH_PLAN_QUERY]: {
    id: DataToolType.WEALTH_PLAN_QUERY,
    description:
      'Returns wealth-plan source data (family, milestones, ownership, goals, wealth balance, cash-flow events, investment profile, and expert inputs) for explanation and guidance.',
    payloadSchema: DATA_TOOL_PAYLOAD_SCHEMAS[DataToolType.WEALTH_PLAN_QUERY],
    examples: [{ question: 'Explain my wealth plan and key priorities.' }],
  },
});

export const UI_ACTION_REGISTRY = Object.freeze(
  Object.values(UI_ACTION_METADATA)
);
export const DATA_TOOL_REGISTRY = Object.freeze(
  Object.values(DATA_TOOL_METADATA)
);

const toolFunctionNameFromId = (id, prefix) =>
  `${prefix}${id}`
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

export const buildOpenAIFunctionSchema = (metadata, options = {}) => {
  const prefix = typeof options.prefix === 'string' ? options.prefix : '';
  const name =
    typeof options.name === 'string' && options.name.length > 0
      ? options.name
      : toolFunctionNameFromId(metadata.id, prefix);

  return {
    name,
    description: metadata.description,
    parameters: metadata.payloadSchema,
    strict: options.strict !== false,
  };
};

export const buildOpenAIToolSchema = (metadata, options = {}) => ({
  type: 'function',
  function: buildOpenAIFunctionSchema(metadata, options),
});

export const buildOpenAIUIActionToolSchemas = (options = {}) =>
  UI_ACTION_REGISTRY.map((metadata) => {
    const { name: _ignoredName, ...restOptions } = options;

    return buildOpenAIToolSchema(metadata, {
      ...restOptions,
      prefix:
        typeof restOptions.prefix === 'string'
          ? restOptions.prefix
          : 'ui_action_',
    });
  });

export const buildOpenAIDataToolSchemas = (options = {}) =>
  DATA_TOOL_REGISTRY.map((metadata) => {
    const { name: _ignoredName, ...restOptions } = options;

    return buildOpenAIToolSchema(metadata, {
      ...restOptions,
      prefix:
        typeof restOptions.prefix === 'string'
          ? restOptions.prefix
          : 'data_tool_',
    });
  });

const UI_ACTION_REQUEST_SCHEMA = createObjectPayloadSchema({
  properties: {
    type: {
      type: 'string',
      enum: Object.freeze(Object.values(UIActionType)),
    },
    payload: { type: 'object' },
  },
  required: ['type', 'payload'],
});

const DATA_TOOL_REQUEST_SCHEMA = createObjectPayloadSchema({
  properties: {
    type: {
      type: 'string',
      enum: Object.freeze(Object.values(DataToolType)),
    },
    payload: { type: 'object' },
  },
  required: ['type', 'payload'],
});

export const ASSISTANT_TURN_SCHEMA = createObjectPayloadSchema({
  properties: {
    reply: { type: 'string' },
    ui_actions: {
      type: 'array',
      items: UI_ACTION_REQUEST_SCHEMA,
    },
    tool_results: {
      type: 'array',
      items: { type: 'object' },
    },
  },
  required: ['reply', 'ui_actions', 'tool_results'],
});

const CLIENT_AI_ACCOUNT_SCHEMA = createObjectPayloadSchema({
  properties: {
    name: { type: 'string' },
    ssid: { type: 'string' },
  },
  required: ['name', 'ssid'],
});

const CLIENT_AI_CURRENT_VIEW_SCHEMA = createObjectPayloadSchema({
  properties: {
    route: { type: 'string' },
    viewId: { type: 'string' },
    entityType: { type: 'string' },
  },
  required: ['route'],
});

const CLIENT_AI_PORTFOLIO_SCHEMA = createObjectPayloadSchema({
  properties: {
    activeFilters: {
      type: 'object',
      additionalProperties: true,
    },
    selectedSsids: {
      type: 'array',
      items: { type: 'string' },
    },
    selectedAccounts: {
      type: 'array',
      items: CLIENT_AI_ACCOUNT_SCHEMA,
    },
    accessibleAccounts: {
      type: 'array',
      items: CLIENT_AI_ACCOUNT_SCHEMA,
    },
  },
});

const CLIENT_AI_REPORTING_SCHEMA = createObjectPayloadSchema({
  properties: {
    reportId: { type: 'string' },
    activeFilters: {
      type: 'object',
      additionalProperties: true,
    },
  },
});

const CLIENT_AI_WEALTH_PLAN_SCHEMA = createObjectPayloadSchema({
  properties: {
    section: { type: 'string' },
    activeFilters: {
      type: 'object',
      additionalProperties: true,
    },
  },
});

const CLIENT_AI_UI_CAPABILITIES_SCHEMA = createObjectPayloadSchema({
  properties: {
    canNavigate: { type: 'boolean' },
    canApplyFilters: { type: 'boolean' },
  },
  required: ['canNavigate', 'canApplyFilters'],
});

export const CLIENT_AI_CONTEXT_SCHEMA = createObjectPayloadSchema({
  properties: {
    platform: {
      type: 'string',
      enum: Object.freeze(['web', 'mobile']),
    },
    locale: { type: 'string' },
    timezone: { type: 'string' },
    currentView: CLIENT_AI_CURRENT_VIEW_SCHEMA,
    portfolio: CLIENT_AI_PORTFOLIO_SCHEMA,
    reporting: CLIENT_AI_REPORTING_SCHEMA,
    wealthPlan: CLIENT_AI_WEALTH_PLAN_SCHEMA,
    uiCapabilities: CLIENT_AI_UI_CAPABILITIES_SCHEMA,
  },
  required: ['platform', 'locale', 'timezone', 'currentView', 'uiCapabilities'],
});

const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;

const isObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const createActionResultOk = (result) => ({
  status: 'ok',
  result,
});

export const createActionResultError = (code, message, details) => ({
  status: 'error',
  error: {
    code,
    message,
    ...(details === undefined ? {} : { details }),
  },
});

export const parseToISODate = (value) => {
  if (typeof value !== 'string') return null;

  if (isoDatePattern.test(value)) {
    return value;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString().split('T')[0];
};

const normalizeLanguageCode = (value) =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

export const isSupportedLanguageCode = (value) =>
  SUPPORTED_LANGUAGE_CODES.includes(normalizeLanguageCode(value));

const normalizeNavigationPath = (value) => {
  if (typeof value !== 'string') return '';

  let path = value.trim();
  if (!path) return '';

  if (!path.startsWith('/')) {
    path = `/${path}`;
  }

  path = path.replace(/\/{2,}/g, '/');

  if (path.length > 1 && path.endsWith('/')) {
    path = path.slice(0, -1);
  }

  return path;
};

const splitNavigationPath = (value) => {
  let path = typeof value === 'string' ? value : '';
  let search = '';
  let hash = '';

  const hashIndex = path.indexOf('#');
  if (hashIndex >= 0) {
    hash = path.slice(hashIndex);
    path = path.slice(0, hashIndex);
  }

  const searchIndex = path.indexOf('?');
  if (searchIndex >= 0) {
    search = path.slice(searchIndex);
    path = path.slice(0, searchIndex);
  }

  return {
    path: normalizeNavigationPath(path),
    search,
    hash,
  };
};

const normalizeNavigationText = (value) => {
  if (typeof value !== 'string') return '';

  return value
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/[Øø]/g, 'o')
    .replace(/[Ææ]/g, 'ae')
    .replace(/[Åå]/g, 'a')
    .replace(/[Öö]/g, 'o')
    .replace(/[Ää]/g, 'a')
    .replace(/[Üü]/g, 'u')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
};

const normalizeNavigationKey = (value) =>
  normalizeNavigationText(value).replace(/\s+/g, '');

const toNavigationTokens = (value) =>
  normalizeNavigationText(value).split(' ').filter(Boolean);

const toSegments = (value) =>
  normalizeNavigationPath(value).toLowerCase().split('/').filter(Boolean);

const hasDynamicRouteSegment = (routePath) =>
  routePath.includes(':') || routePath.includes('*');

const isDynamicSegment = (segment) =>
  segment.startsWith(':') || segment === '*';

const matchPathAgainstPattern = (path, pattern) => {
  const pathSegments = toSegments(path);
  const patternSegments = toSegments(pattern);

  if (!patternSegments.length) {
    return !pathSegments.length;
  }

  if (patternSegments[patternSegments.length - 1] === '*') {
    const prefix = patternSegments.slice(0, -1);
    if (pathSegments.length < prefix.length) {
      return false;
    }
    return prefix.every((segment, index) =>
      isDynamicSegment(segment)
        ? Boolean(pathSegments[index])
        : pathSegments[index] === segment
    );
  }

  if (pathSegments.length !== patternSegments.length) {
    return false;
  }

  return patternSegments.every((segment, index) =>
    isDynamicSegment(segment)
      ? Boolean(pathSegments[index])
      : pathSegments[index] === segment
  );
};

export const WEB_ROUTE_PATTERNS = Object.freeze([
  '/',
  '/events',
  '/events/:id',
  '/webinars',
  '/webinars/:id',
  '/podcasts',
  '/todo',
  '/todo/:type/:id',
  '/wealth-plan',
  '/wealth-plan/goals',
  '/wealth-plan/family-and-ownership',
  '/wealth-plan/wealth-balance',
  '/wealth-plan/investment-profile',
  '/wealth-plan/investment-strategies',
  '/wealth-plan/investment-strategies/strategies',
  '/wealth-plan/investment-strategies/investment-simulator',
  '/wealth-plan/expert-input',
  '/investment-strategy/:id',
  '/portfolio/periodic-reporting/export',
  '/portfolio',
  '/portfolio/holdings',
  '/portfolio/holdings/securities',
  '/portfolio/transactions',
  '/portfolio/strategy',
  '/portfolio/lenses',
  '/portfolio/lenses/portfolio-construction',
  '/portfolio/lenses/sustainability',
  '/portfolio/lenses/geography',
  '/portfolio/lenses/sectors',
  '/portfolio/factsheets',
  '/portfolio/periodic-reporting',
  '/portfolio/periodic-reporting/transactions-reporting',
  '/portfolio/periodic-reporting/alternative-assets-reporting',
  '/portfolio/reporting',
  '/portfolio/reporting/tax/2024',
  '/portfolio/reporting/tax/:year',
  '/portfolio/reporting/tax/declaration/:year',
  '/portfolio/reporting/cost',
  '/list/funds-list',
  '/list/companies-list',
  '/documents',
  '/profile',
  '/profile/self-declaration',
  '/profile/accounts/:id',
  '/account-team',
  '/contact',
  '/report/:id',
]);

export const MOBILE_STACK_SCREENS = Object.freeze({
  HomeStack: Object.freeze([
    'Home',
    'Article',
    'Event',
    'Webinar',
    'PushNotification',
    'TaxInfoGeneral',
    'TaxReport',
  ]),
  PortfolioStack: Object.freeze([
    'Portfolio',
    'PortfolioHoldings',
    'Transactions',
    'Sustainability',
    'CarbonFootprint',
    'TemperatureRise',
    'ScienceBasedTargets',
    'SocialResponsibility',
    'FundClassification',
    'Geography',
    'Sectors',
    'Strategy',
    'PortfolioConstruction',
    'AllFunds',
    'Fund',
    'AllCompanies',
    'TaxInfoGeneral',
    'TaxReport',
  ]),
  TodoStack: Object.freeze([
    'Todo',
    'TodoItem',
    'ExpertInputItem',
    'InvestmentStrategy',
  ]),
  MenuStack: Object.freeze([
    'Menu',
    'Documents',
    'Settings',
    'AccountSettings',
    'SelfDeclaration',
    'WealthBalance',
    'Goals',
    'MyTeam',
    'LeadContact',
    'InvestmentStrategies',
    'InvestmentStrategy',
    'InvestmentSimulator',
    'FamilyAndOwnership',
    'Family',
    'Milestone',
    'Network',
    'BusinessAndOwnership',
    'InvestmentProfile',
    'InvestmentPreferences',
    'RiskCapacity',
    'RiskTolerance',
    'RiskProfile',
    'LockInLevel',
    'RiskLevel',
    'AdvisorsComment',
    'AdvisorsCommentOnPreferences',
    'AdvisorsCommentOnRiskAndLockIn',
    'KnowledgeAndTrainingComment',
    'ExpertInput',
  ]),
});

const MOBILE_ROOT_SCREENS = Object.freeze([
  'Main',
  'Registration',
  'EmployeeClientSelect',
  'AnnualReviewReport',
]);

export const NAVIGATION_TARGETS = Object.freeze({
  home: Object.freeze({
    id: 'home',
    aliases: Object.freeze(['home', 'hjem', 'hem', 'start', '/']),
    webPath: '/',
    mobile: Object.freeze({
      root: 'Main',
      stack: 'HomeStack',
      screen: 'Home',
    }),
  }),
  documents: Object.freeze({
    id: 'documents',
    aliases: Object.freeze([
      'documents',
      'document',
      'dokuments',
      'dokument',
      'dokumenter',
      'mine dokumenter',
      'mina dokument',
      'docs',
      '/documents',
    ]),
    webPath: '/documents',
    mobile: Object.freeze({
      root: 'Main',
      stack: 'MenuStack',
      screen: 'Documents',
    }),
  }),
  portfolio: Object.freeze({
    id: 'portfolio',
    aliases: Object.freeze([
      'portfolio',
      'portefolje',
      'portefølje',
      'portfolje',
      'portfölj',
      '/portfolio',
    ]),
    webPath: '/portfolio',
    mobile: Object.freeze({
      root: 'Main',
      stack: 'PortfolioStack',
      screen: 'Portfolio',
    }),
  }),
  'portfolio.holdings': Object.freeze({
    id: 'portfolio.holdings',
    aliases: Object.freeze([
      'portfolio holdings',
      'holdings',
      'beholdninger',
      'innehav',
      '/portfolio/holdings',
    ]),
    webPath: '/portfolio/holdings',
    mobile: Object.freeze({
      root: 'Main',
      stack: 'PortfolioStack',
      screen: 'PortfolioHoldings',
    }),
  }),
  'portfolio.transactions': Object.freeze({
    id: 'portfolio.transactions',
    aliases: Object.freeze([
      'portfolio transactions',
      'transactions',
      'transaction',
      'transaksjoner',
      'transaksjon',
      'transaktioner',
      'transaktion',
      '/transactions',
      '/portfolio/transactions',
      'PortfolioStack.Transactions',
    ]),
    webPath: '/portfolio/transactions',
    mobile: Object.freeze({
      root: 'Main',
      stack: 'PortfolioStack',
      screen: 'Transactions',
    }),
  }),
  'portfolio.strategy': Object.freeze({
    id: 'portfolio.strategy',
    aliases: Object.freeze([
      'portfolio strategy',
      'strategy',
      'strategi',
      '/portfolio/strategy',
    ]),
    webPath: '/portfolio/strategy',
    mobile: Object.freeze({
      root: 'Main',
      stack: 'PortfolioStack',
      screen: 'Strategy',
    }),
  }),
  'portfolio.sustainability': Object.freeze({
    id: 'portfolio.sustainability',
    aliases: Object.freeze([
      'sustainability',
      'baerekraft',
      'bærekraft',
      'hallbarhet',
      'hållbarhet',
      'portfolio sustainability',
      '/portfolio/lenses/sustainability',
    ]),
    webPath: '/portfolio/lenses/sustainability',
    mobile: Object.freeze({
      root: 'Main',
      stack: 'PortfolioStack',
      screen: 'Sustainability',
    }),
  }),
  'portfolio.geography': Object.freeze({
    id: 'portfolio.geography',
    aliases: Object.freeze([
      'geography',
      'geografi',
      '/portfolio/lenses/geography',
    ]),
    webPath: '/portfolio/lenses/geography',
    mobile: Object.freeze({
      root: 'Main',
      stack: 'PortfolioStack',
      screen: 'Geography',
    }),
  }),
  'portfolio.sectors': Object.freeze({
    id: 'portfolio.sectors',
    aliases: Object.freeze([
      'sectors',
      'sektorer',
      '/portfolio/lenses/sectors',
    ]),
    webPath: '/portfolio/lenses/sectors',
    mobile: Object.freeze({
      root: 'Main',
      stack: 'PortfolioStack',
      screen: 'Sectors',
    }),
  }),
  'portfolio.construction': Object.freeze({
    id: 'portfolio.construction',
    aliases: Object.freeze([
      'portfolio construction',
      'portfolio content',
      'portefoljeinnhold',
      'porteføljeinnhold',
      'portfoljinnehall',
      'portföljinnehåll',
      '/portfolio/lenses/portfolio-construction',
    ]),
    webPath: '/portfolio/lenses/portfolio-construction',
    mobile: Object.freeze({
      root: 'Main',
      stack: 'PortfolioStack',
      screen: 'PortfolioConstruction',
    }),
  }),
  'portfolio.factsheets': Object.freeze({
    id: 'portfolio.factsheets',
    aliases: Object.freeze([
      'factsheets',
      'fact sheets',
      'faktaark',
      'faktablad',
      '/portfolio/factsheets',
    ]),
    webPath: '/portfolio/factsheets',
  }),
  'portfolio.periodicReporting': Object.freeze({
    id: 'portfolio.periodicReporting',
    aliases: Object.freeze([
      'periodic reporting',
      'periodisk rapportering',
      'lagstadgad rapportering',
      'lovpligtig rapportering',
      'lovpalegt rapportering',
      'lovpålagt rapportering',
      '/portfolio/periodic-reporting',
    ]),
    webPath: '/portfolio/periodic-reporting',
  }),
  'portfolio.periodicReporting.transactions': Object.freeze({
    id: 'portfolio.periodicReporting.transactions',
    aliases: Object.freeze([
      'transactions reporting',
      'transaksjonsrapportering',
      'transaktionsrapportering',
      '/portfolio/periodic-reporting/transactions-reporting',
    ]),
    webPath: '/portfolio/periodic-reporting/transactions-reporting',
  }),
  'portfolio.periodicReporting.alternativeAssets': Object.freeze({
    id: 'portfolio.periodicReporting.alternativeAssets',
    aliases: Object.freeze([
      'alternative assets reporting',
      'rapportering alternative aktiva',
      'alternative aktiver rapportering',
      '/portfolio/periodic-reporting/alternative-assets-reporting',
    ]),
    webPath: '/portfolio/periodic-reporting/alternative-assets-reporting',
  }),
  'portfolio.reporting': Object.freeze({
    id: 'portfolio.reporting',
    aliases: Object.freeze([
      'reporting',
      'rapporter',
      'rapportering',
      '/portfolio/reporting',
    ]),
    webPath: '/portfolio/reporting',
  }),
  'portfolio.reporting.tax': Object.freeze({
    id: 'portfolio.reporting.tax',
    aliases: Object.freeze([
      'tax',
      'tax reporting',
      'tax report',
      'skatt',
      'skatterapportering',
      'skatteindberetning',
      'skattedeklaration',
      '/portfolio/reporting/tax/2024',
    ]),
    webPath: '/portfolio/reporting/tax/2024',
  }),
  'portfolio.reporting.cost': Object.freeze({
    id: 'portfolio.reporting.cost',
    aliases: Object.freeze([
      'cost report',
      'cost reporting',
      'kostnadsrapport',
      'kostnadsrapportering',
      'omkostningsrapport',
      '/portfolio/reporting/cost',
    ]),
    webPath: '/portfolio/reporting/cost',
  }),
  todo: Object.freeze({
    id: 'todo',
    aliases: Object.freeze([
      'todo',
      'to do',
      'tasks',
      'oppgaver',
      'uppgifter',
      'opgaver',
      '/todo',
    ]),
    webPath: '/todo',
    mobile: Object.freeze({
      root: 'Main',
      stack: 'TodoStack',
      screen: 'Todo',
    }),
  }),
  events: Object.freeze({
    id: 'events',
    aliases: Object.freeze(['events', 'nyheter', 'hendelser', '/events']),
    webPath: '/events',
  }),
  webinars: Object.freeze({
    id: 'webinars',
    aliases: Object.freeze(['webinars', 'webinar', '/webinars']),
    webPath: '/webinars',
  }),
  podcasts: Object.freeze({
    id: 'podcasts',
    aliases: Object.freeze(['podcasts', 'podcast', '/podcasts']),
    webPath: '/podcasts',
  }),
  'wealthPlan.overview': Object.freeze({
    id: 'wealthPlan.overview',
    aliases: Object.freeze([
      'wealth plan',
      'wealthplan',
      'formuesplan',
      'forvaltningsplan',
      'formueplan',
      'formogenhetsplan',
      'förmögenhetsplan',
      '/wealth-plan',
    ]),
    webPath: '/wealth-plan',
  }),
  'wealthPlan.goals': Object.freeze({
    id: 'wealthPlan.goals',
    aliases: Object.freeze([
      'goals',
      'goals and priorities',
      'mal og prioriteringer',
      'mål og prioriteringer',
      'mal och prioriteringar',
      'mål och prioriteringar',
      '/wealth-plan/goals',
    ]),
    webPath: '/wealth-plan/goals',
  }),
  'wealthPlan.familyAndOwnership': Object.freeze({
    id: 'wealthPlan.familyAndOwnership',
    aliases: Object.freeze([
      'family and ownership',
      'familie og eierskap',
      'familj och agande',
      'familj och ägande',
      'familie og ejerskab',
      '/wealth-plan/family-and-ownership',
    ]),
    webPath: '/wealth-plan/family-and-ownership',
  }),
  'wealthPlan.wealthBalance': Object.freeze({
    id: 'wealthPlan.wealthBalance',
    aliases: Object.freeze([
      'wealth balance',
      'formue',
      'formuesbalanse',
      'formuesbalance',
      'förmögenhetsbalans',
      '/wealth-plan/wealth-balance',
    ]),
    webPath: '/wealth-plan/wealth-balance',
  }),
  'wealthPlan.investmentProfile': Object.freeze({
    id: 'wealthPlan.investmentProfile',
    aliases: Object.freeze([
      'investment profile',
      'investeringsprofil',
      '/wealth-plan/investment-profile',
    ]),
    webPath: '/wealth-plan/investment-profile',
  }),
  'wealthPlan.investmentStrategies': Object.freeze({
    id: 'wealthPlan.investmentStrategies',
    aliases: Object.freeze([
      'investment strategy',
      'investment strategies',
      'investeringsstrategi',
      'investeringsstrategier',
      '/wealth-plan/investment-strategies',
      '/wealth-plan/investment-strategies/strategies',
    ]),
    webPath: '/wealth-plan/investment-strategies',
  }),
  'wealthPlan.investmentSimulator': Object.freeze({
    id: 'wealthPlan.investmentSimulator',
    aliases: Object.freeze([
      'investment simulator',
      'investeringssimulator',
      '/wealth-plan/investment-strategies/investment-simulator',
    ]),
    webPath: '/wealth-plan/investment-strategies/investment-simulator',
  }),
  'wealthPlan.expertInput': Object.freeze({
    id: 'wealthPlan.expertInput',
    aliases: Object.freeze([
      'expert input',
      'expert advice',
      'ekspertrad',
      'ekspertråd',
      '/wealth-plan/expert-input',
    ]),
    webPath: '/wealth-plan/expert-input',
  }),
  profile: Object.freeze({
    id: 'profile',
    aliases: Object.freeze(['profile', 'profil', 'min profil', '/profile']),
    webPath: '/profile',
  }),
  settings: Object.freeze({
    id: 'settings',
    aliases: Object.freeze([
      'settings',
      'innstillinger',
      'inställningar',
      'indstillinger',
    ]),
    webPath: '/profile',
    mobile: Object.freeze({
      root: 'Main',
      stack: 'MenuStack',
      screen: 'Settings',
    }),
  }),
  accountTeam: Object.freeze({
    id: 'accountTeam',
    aliases: Object.freeze([
      'my team',
      'mitt team',
      'mit team',
      'account team',
      '/account-team',
    ]),
    webPath: '/account-team',
  }),
  contact: Object.freeze({
    id: 'contact',
    aliases: Object.freeze(['contact', 'kontakt', '/contact']),
    webPath: '/contact',
  }),
  'list.funds': Object.freeze({
    id: 'list.funds',
    aliases: Object.freeze([
      'funds list',
      'fund list',
      'fondsliste',
      'fondlista',
      '/list/funds-list',
    ]),
    webPath: '/list/funds-list',
  }),
  'list.companies': Object.freeze({
    id: 'list.companies',
    aliases: Object.freeze([
      'companies list',
      'company list',
      'selskapsliste',
      'bolagslista',
      '/list/companies-list',
    ]),
    webPath: '/list/companies-list',
  }),
});

const navigationTargetList = Object.freeze(Object.values(NAVIGATION_TARGETS));

const candidateWeightByType = Object.freeze({
  alias: 1,
  id: 0.95,
  pathLeaf: 0.75,
  path: 0.65,
});

const buildTargetSearchCandidates = (target) => {
  const candidates = [];
  const seen = new Set();

  const addCandidate = (value, type) => {
    const phrase = normalizeNavigationText(value);
    const key = normalizeNavigationKey(phrase);
    if (!phrase || !key) return;

    const signature = `${type}:${key}`;
    if (seen.has(signature)) return;
    seen.add(signature);

    candidates.push({
      phrase,
      key,
      type,
      tokens: toNavigationTokens(phrase),
    });
  };

  addCandidate(target.id, 'id');
  addCandidate(target.id.replace(/[._-]/g, ' '), 'id');
  target.aliases.forEach((alias) => addCandidate(alias, 'alias'));

  if (target.webPath) {
    addCandidate(target.webPath, 'path');

    const segments = toSegments(target.webPath).filter(
      (segment) => !isDynamicSegment(segment)
    );

    if (segments.length) {
      addCandidate(segments.join(' '), 'path');
      addCandidate(segments[segments.length - 1], 'pathLeaf');
    }
  }

  return candidates;
};

const targetSearchIndex = Object.freeze(
  navigationTargetList.map((target) =>
    Object.freeze({
      target,
      candidates: Object.freeze(buildTargetSearchCandidates(target)),
    })
  )
);

const sharedTokenCount = (queryTokens, candidateTokens) => {
  if (!queryTokens.length || !candidateTokens.length) return 0;

  const candidateSet = new Set(candidateTokens);
  return queryTokens.filter((token) => candidateSet.has(token)).length;
};

const getTargetScore = (query, searchEntry) => {
  const queryPhrase = normalizeNavigationText(query);
  const queryKey = normalizeNavigationKey(query);
  const queryTokens = toNavigationTokens(query);

  if (!queryKey) {
    return 0;
  }

  let bestScore = 0;

  searchEntry.candidates.forEach((candidate) => {
    if (!candidate.key) return;

    const weight = candidateWeightByType[candidate.type] || 0.6;
    let score = 0;

    if (candidate.key === queryKey) {
      score = 1000;
    } else {
      if (
        queryKey.length >= 3 &&
        (candidate.key.startsWith(queryKey) ||
          queryKey.startsWith(candidate.key))
      ) {
        score = Math.max(score, 740);
      }

      if (
        queryKey.length >= 4 &&
        (candidate.key.includes(queryKey) || queryKey.includes(candidate.key))
      ) {
        score = Math.max(score, 630);
      }

      if (queryPhrase && candidate.phrase.includes(queryPhrase)) {
        score = Math.max(score, 660);
      }
    }

    const sharedTokens = sharedTokenCount(queryTokens, candidate.tokens);
    if (sharedTokens > 0) {
      score += sharedTokens * 120;

      if (sharedTokens === queryTokens.length) {
        score += 110;
      }

      score -= Math.max(0, candidate.tokens.length - queryTokens.length) * 10;
    }

    score *= weight;
    bestScore = Math.max(bestScore, score);
  });

  return bestScore;
};

const resolveFromNavigationTargets = (query, platform) => {
  const trimmedQuery = typeof query === 'string' ? query.trim() : '';
  const normalizedQuery = normalizeNavigationKey(trimmedQuery);
  const isPathLikeQuery = trimmedQuery.includes('/');

  let match = navigationTargetList.find((target) => {
    if (target.id.toLowerCase() === trimmedQuery.toLowerCase()) {
      return true;
    }

    return target.aliases.some(
      (alias) => normalizeNavigationKey(alias) === normalizedQuery
    );
  });

  if (!match && !isPathLikeQuery) {
    const rankedMatches = targetSearchIndex
      .map((searchEntry) => ({
        target: searchEntry.target,
        score: getTargetScore(trimmedQuery, searchEntry),
      }))
      .filter((entry) => entry.score >= 260)
      .sort((a, b) => b.score - a.score);

    const bestMatch = rankedMatches[0];
    const secondBestMatch = rankedMatches[1];

    if (!bestMatch) {
      return null;
    }

    if (secondBestMatch && secondBestMatch.score >= bestMatch.score - 35) {
      return {
        status: 'error',
        message: `Navigation query "${trimmedQuery}" is ambiguous.`,
        candidates: rankedMatches
          .slice(0, 5)
          .map((entry) => entry.target.webPath || entry.target.id),
      };
    }

    match = bestMatch.target;
  }

  if (!match) {
    return null;
  }

  if (platform === 'web' && match.webPath) {
    return {
      status: 'ok',
      targetId: match.id,
      webPath: match.webPath,
      matchedBy: 'target',
    };
  }

  if (platform === 'mobile' && match.mobile) {
    return {
      status: 'ok',
      targetId: match.id,
      mobile: match.mobile,
      matchedBy: 'target',
    };
  }

  return {
    status: 'error',
    message: `Target "${match.id}" is not available on ${platform}.`,
  };
};

const resolveWebRouteQuery = (query) => {
  const { path } = splitNavigationPath(query);
  if (!path) {
    return {
      status: 'error',
      message: 'Route path must be a non-empty string.',
    };
  }

  const lowerPath = path.toLowerCase();

  const exactMatch = WEB_ROUTE_PATTERNS.find(
    (routePath) => routePath.toLowerCase() === lowerPath
  );
  if (exactMatch) {
    return {
      status: 'ok',
      webPath: exactMatch,
      matchedBy: 'exact',
    };
  }

  const patternMatch = WEB_ROUTE_PATTERNS.find(
    (routePath) =>
      hasDynamicRouteSegment(routePath) &&
      matchPathAgainstPattern(path, routePath)
  );
  if (patternMatch) {
    return {
      status: 'ok',
      webPath: path,
      matchedBy: 'pattern',
      pattern: patternMatch,
    };
  }

  const targetMatch = resolveFromNavigationTargets(query, 'web');
  if (targetMatch) {
    if (targetMatch.status === 'error') {
      return targetMatch;
    }

    if (targetMatch.webPath) {
      return {
        status: 'ok',
        webPath: targetMatch.webPath,
        matchedBy: 'target-alias',
        targetId: targetMatch.targetId,
      };
    }
  }

  return {
    status: 'error',
    message: `Could not resolve "${query}" to a known web route.`,
  };
};

const buildMobileScreenEntries = () => {
  const entries = [];

  MOBILE_ROOT_SCREENS.forEach((screen) => {
    entries.push({
      key: screen,
      screen,
      mobile: {
        screen,
      },
    });
  });

  Object.keys(MOBILE_STACK_SCREENS).forEach((stack) => {
    MOBILE_STACK_SCREENS[stack].forEach((screen) => {
      entries.push({
        key: `${stack}.${screen}`,
        screen,
        stack,
        mobile: {
          root: 'Main',
          stack,
          screen,
        },
      });
    });
  });

  return entries;
};

const mobileScreenEntries = buildMobileScreenEntries();

export const MOBILE_SCREEN_CATALOG = Object.freeze(
  mobileScreenEntries.map((entry) => Object.freeze({ ...entry }))
);

const resolveMobileScreenQuery = (query) => {
  const trimmed = typeof query === 'string' ? query.trim() : '';
  if (!trimmed) {
    return {
      status: 'error',
      message: 'Screen name must be a non-empty string.',
    };
  }

  const stackScreenMatch = trimmed.match(
    /^([A-Za-z0-9_]+)[./]([A-Za-z0-9_]+)$/
  );
  if (stackScreenMatch) {
    const [, stack, screen] = stackScreenMatch;
    const stackEntry = MOBILE_SCREEN_CATALOG.find(
      (entry) =>
        entry.stack?.toLowerCase() === stack.toLowerCase() &&
        entry.screen.toLowerCase() === screen.toLowerCase()
    );
    if (stackEntry) {
      return {
        status: 'ok',
        mobile: stackEntry.mobile,
        matchedBy: 'stack-screen',
      };
    }
  }

  const normalizedQuery = normalizeNavigationKey(trimmed);
  const matches = MOBILE_SCREEN_CATALOG.filter(
    (entry) =>
      entry.screen.toLowerCase() === trimmed.toLowerCase() ||
      normalizeNavigationKey(entry.screen) === normalizedQuery
  );

  if (matches.length === 1) {
    return {
      status: 'ok',
      mobile: matches[0].mobile,
      matchedBy: 'screen',
    };
  }

  if (matches.length > 1) {
    return {
      status: 'error',
      message: `Screen "${trimmed}" is ambiguous in mobile navigation.`,
      candidates: matches.slice(0, 5).map((item) => item.key),
    };
  }

  return {
    status: 'error',
    message: `Could not resolve "${trimmed}" to a known mobile screen.`,
  };
};

export const resolveNavigationTarget = (query, platform) => {
  if (platform !== 'web' && platform !== 'mobile') {
    return {
      status: 'error',
      message: `Unsupported navigation platform "${platform}".`,
    };
  }

  const targetMatch = resolveFromNavigationTargets(query, platform);
  if (targetMatch) {
    return targetMatch;
  }

  if (platform === 'web') {
    return resolveWebRouteQuery(query);
  }

  const webMatch = resolveWebRouteQuery(query);
  if (webMatch.status === 'ok') {
    const linkedTarget = navigationTargetList.find(
      (target) => target.webPath === webMatch.webPath && target.mobile
    );

    if (linkedTarget) {
      return {
        status: 'ok',
        targetId: linkedTarget.id,
        mobile: linkedTarget.mobile,
        matchedBy: 'linked-web-route',
      };
    }
  }

  return resolveMobileScreenQuery(query);
};

export const isUIActionType = (value) =>
  Object.values(UIActionType).includes(value);

export const isDataToolType = (value) =>
  Object.values(DataToolType).includes(value);

const isObjectOrUndefined = (value) =>
  value === undefined || value === null || isObject(value);

const isStringOrNumber = (value) =>
  typeof value === 'string' || typeof value === 'number';

const isArrayOfStringOrNumber = (value) =>
  Array.isArray(value) && value.every(isStringOrNumber);

const isArrayOfStrings = (value) =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const isOptionalDateInput = (value) =>
  value === undefined || value === null || parseToISODate(value) !== null;

const isIntegerInRange = (value, min, max) =>
  Number.isInteger(value) && value >= min && value <= max;

const isActionError = (value) =>
  isObject(value) &&
  typeof value.code === 'string' &&
  value.code.length > 0 &&
  typeof value.message === 'string' &&
  value.message.length > 0;

const isOptionalString = (value) =>
  value === undefined || value === null || typeof value === 'string';

const isNonEmptyString = (value) =>
  typeof value === 'string' && value.trim().length > 0;

const hasOwnKey = (value, key) =>
  Object.prototype.hasOwnProperty.call(value, key);

const hasOnlyAllowedKeys = (payload, allowedKeys) =>
  Object.keys(payload).every((key) => allowedKeys.includes(key));

const isStrictObjectShape = (
  payload,
  { requiredKeys = [], optionalKeys = [] }
) => {
  if (!isObject(payload)) return false;

  if (!requiredKeys.every((key) => hasOwnKey(payload, key))) {
    return false;
  }

  return hasOnlyAllowedKeys(payload, [...requiredKeys, ...optionalKeys]);
};

export const isPortfolioSetDateRangePayload = (payload) => {
  if (
    !isStrictObjectShape(payload, {
      requiredKeys: ['from', 'to'],
      optionalKeys: [],
    })
  ) {
    return false;
  }

  const from = parseToISODate(payload.from);
  const to = parseToISODate(payload.to);

  return Boolean(from && to);
};

export const isPortfolioSetDateRangeAction = (action) =>
  isObject(action) &&
  action.type === UIActionType.PORTFOLIO_SET_DATE_RANGE &&
  isPortfolioSetDateRangePayload(action.payload);

export const isNavigateTargetPayload = (payload) =>
  isStrictObjectShape(payload, {
    requiredKeys: ['targetId'],
    optionalKeys: ['params', 'search', 'hash'],
  }) &&
  typeof payload.targetId === 'string' &&
  payload.targetId.trim().length > 0 &&
  (payload.params === undefined ||
    payload.params === null ||
    isObject(payload.params)) &&
  (payload.search === undefined || typeof payload.search === 'string') &&
  (payload.hash === undefined || typeof payload.hash === 'string');

export const isNavigateRoutePayload = (payload) =>
  isStrictObjectShape(payload, {
    requiredKeys: ['path'],
    optionalKeys: ['search', 'hash'],
  }) &&
  typeof payload.path === 'string' &&
  payload.path.trim().length > 0 &&
  (payload.search === undefined || typeof payload.search === 'string') &&
  (payload.hash === undefined || typeof payload.hash === 'string');

export const isNavigateScreenPayload = (payload) =>
  isStrictObjectShape(payload, {
    requiredKeys: ['name'],
    optionalKeys: ['params'],
  }) &&
  typeof payload.name === 'string' &&
  payload.name.length > 0 &&
  isObjectOrUndefined(payload.params);

export const isPortfolioFilterPeriodPayload = (payload) =>
  isStrictObjectShape(payload, {
    requiredKeys: ['period'],
    optionalKeys: ['startDate', 'endDate'],
  }) &&
  typeof payload.period === 'string' &&
  isOptionalDateInput(payload.startDate) &&
  isOptionalDateInput(payload.endDate);

export const isPortfolioSetFiltersPayload = (payload) => isObject(payload);

export const isPortfolioFilterSsidsPayload = (payload) =>
  isStrictObjectShape(payload, {
    requiredKeys: ['ssids'],
    optionalKeys: [],
  }) && isArrayOfStringOrNumber(payload.ssids);

export const isPortfolioFilterExternalReportingPayload = (payload) =>
  isStrictObjectShape(payload, {
    requiredKeys: ['value'],
    optionalKeys: [],
  }) && typeof payload.value === 'string';

export const isPortfolioFilterTaxClassPayload = (payload) =>
  isStrictObjectShape(payload, {
    requiredKeys: ['value'],
    optionalKeys: [],
  }) && typeof payload.value === 'string';

export const isPortfolioFilterInvestmentAccountPayload = (payload) =>
  isStrictObjectShape(payload, {
    requiredKeys: ['value'],
    optionalKeys: [],
  }) &&
  (typeof payload.value === 'string' || isArrayOfStrings(payload.value));

export const isPortfolioActiveStrategyPayload = (payload) =>
  isStrictObjectShape(payload, {
    requiredKeys: ['strategyId'],
    optionalKeys: [],
  }) &&
  (payload.strategyId === null || isStringOrNumber(payload.strategyId));

export const isFeedFilterPayload = (payload) =>
  isStrictObjectShape(payload, {
    requiredKeys: ['type'],
    optionalKeys: [],
  }) && typeof payload.type === 'string';

export const isDocumentsFilterRelationshipsPayload = (payload) =>
  isStrictObjectShape(payload, {
    requiredKeys: ['relationships'],
    optionalKeys: [],
  }) && isArrayOfStringOrNumber(payload.relationships);

export const isDocumentsFilterYearsPayload = (payload) =>
  isStrictObjectShape(payload, {
    requiredKeys: ['year'],
    optionalKeys: [],
  }) && isStringOrNumber(payload.year);

export const isDocumentsFilterTypesPayload = (payload) =>
  isStrictObjectShape(payload, {
    requiredKeys: ['documentTypes'],
    optionalKeys: [],
  }) && isArrayOfStrings(payload.documentTypes);

export const isDocumentsResetFiltersPayload = (payload) =>
  isStrictObjectShape(payload, {
    requiredKeys: [],
    optionalKeys: [],
  });

export const isDocumentsShowOnlyUnreadPayload = (payload) =>
  isStrictObjectShape(payload, {
    requiredKeys: ['value'],
    optionalKeys: [],
  }) && typeof payload.value === 'boolean';

export const isUserSetLanguagePayload = (payload) =>
  isStrictObjectShape(payload, {
    requiredKeys: ['language'],
    optionalKeys: [],
  }) && isSupportedLanguageCode(payload.language);

const optional = (validator) => (value) =>
  value === undefined || validator(value);

const isFiniteNumber = (value) =>
  typeof value === 'number' && Number.isFinite(value);

const isSupportedPortfolioToolPeriod = (value) =>
  typeof value === 'string' && PORTFOLIO_SUPPORTED_TOOL_PERIODS.includes(value);
const isReportYear = (value) => isIntegerInRange(value, 1900, 2200);

const DATA_TOOL_PAYLOAD_VALIDATORS = Object.freeze({
  [DataToolType.PORTFOLIO_TOP_PERFORMING_PRODUCTS]: Object.freeze({
    metric: optional((value) => PORTFOLIO_PERFORMANCE_METRICS.includes(value)),
    topN: optional((value) => isIntegerInRange(value, 1, 20)),
    period: optional(isSupportedPortfolioToolPeriod),
    from: optional((value) => parseToISODate(value) !== null),
    to: optional((value) => parseToISODate(value) !== null),
    ssids: optional(isArrayOfStringOrNumber),
    minMarketValue: optional(isFiniteNumber),
    includeSold: optional((value) => typeof value === 'boolean'),
  }),
  [DataToolType.PORTFOLIO_QUERY]: Object.freeze({
    question: (value) => typeof value === 'string' && value.trim().length > 0,
    dataScopes: optional(isArrayOfStrings),
    period: optional(isSupportedPortfolioToolPeriod),
    from: optional((value) => parseToISODate(value) !== null),
    to: optional((value) => parseToISODate(value) !== null),
    ssids: optional(isArrayOfStringOrNumber),
    includeSold: optional((value) => typeof value === 'boolean'),
    maxRows: optional((value) => isIntegerInRange(value, 1, 2000)),
  }),
  [DataToolType.PORTFOLIO_TRANSACTIONS_QUERY]: Object.freeze({
    question: (value) => typeof value === 'string' && value.trim().length > 0,
    period: optional(isSupportedPortfolioToolPeriod),
    from: optional((value) => parseToISODate(value) !== null),
    to: optional((value) => parseToISODate(value) !== null),
    ssids: optional(isArrayOfStringOrNumber),
    transactionTypes: optional(isArrayOfStrings),
    maxRows: optional((value) => isIntegerInRange(value, 1, 2000)),
    sort: optional((value) =>
      ['date_desc', 'date_asc', 'amount_desc', 'amount_asc'].includes(value)
    ),
  }),
  [DataToolType.PORTFOLIO_ACCESSIBLE_ACCOUNTS]: Object.freeze({}),
  [DataToolType.PORTFOLIO_MARKETVALUES_QUERY]: Object.freeze({
    question: (value) => typeof value === 'string' && value.trim().length > 0,
    period: optional(isSupportedPortfolioToolPeriod),
    from: optional((value) => parseToISODate(value) !== null),
    to: optional((value) => parseToISODate(value) !== null),
    ssids: optional(isArrayOfStringOrNumber),
    granularity: optional((value) => ['MONTH', 'YEAR'].includes(value)),
    maxRows: optional((value) => isIntegerInRange(value, 1, 2000)),
  }),
  [DataToolType.PORTFOLIO_SECURITIES_QUERY]: Object.freeze({
    question: (value) => typeof value === 'string' && value.trim().length > 0,
    period: optional(isSupportedPortfolioToolPeriod),
    from: optional((value) => parseToISODate(value) !== null),
    to: optional((value) => parseToISODate(value) !== null),
    ssids: optional(isArrayOfStringOrNumber),
    maxRows: optional((value) => isIntegerInRange(value, 1, 2000)),
  }),
  [DataToolType.PORTFOLIO_FUNDS_QUERY]: Object.freeze({
    question: (value) => typeof value === 'string' && value.trim().length > 0,
    period: optional(isSupportedPortfolioToolPeriod),
    from: optional((value) => parseToISODate(value) !== null),
    to: optional((value) => parseToISODate(value) !== null),
    ssids: optional(isArrayOfStringOrNumber),
    maxRows: optional((value) => isIntegerInRange(value, 1, 2000)),
  }),
  [DataToolType.PORTFOLIO_COST_REPORT_QUERY]: Object.freeze({
    question: (value) => typeof value === 'string' && value.trim().length > 0,
    year: optional(isReportYear),
    ssids: optional(isArrayOfStringOrNumber),
    maxRows: optional((value) => isIntegerInRange(value, 1, 2000)),
  }),
  [DataToolType.PORTFOLIO_STRATEGY_QUERY]: Object.freeze({
    question: (value) => typeof value === 'string' && value.trim().length > 0,
    period: optional(isSupportedPortfolioToolPeriod),
    from: optional((value) => parseToISODate(value) !== null),
    to: optional((value) => parseToISODate(value) !== null),
    ssids: optional(isArrayOfStringOrNumber),
    maxRows: optional((value) => isIntegerInRange(value, 1, 2000)),
  }),
  [DataToolType.PORTFOLIO_TAX_DETAILS_QUERY]: Object.freeze({
    question: (value) => typeof value === 'string' && value.trim().length > 0,
    year: optional(isReportYear),
    ssids: optional(isArrayOfStringOrNumber),
    maxRows: optional((value) => isIntegerInRange(value, 1, 2000)),
  }),
  [DataToolType.WEALTH_PLAN_QUERY]: Object.freeze({
    question: (value) => typeof value === 'string' && value.trim().length > 0,
    dataScopes: optional(isArrayOfStrings),
    expertInputId: optional(
      (value) => typeof value === 'string' && value.trim().length > 0
    ),
    maxRows: optional((value) => isIntegerInRange(value, 1, 500)),
  }),
});

const validateDataToolPayloadByType = (type, payload) => {
  const validatorsByField = DATA_TOOL_PAYLOAD_VALIDATORS[type];
  if (!validatorsByField) {
    return isObject(payload);
  }

  if (!isObject(payload)) {
    return false;
  }

  const allowedKeys = Object.keys(validatorsByField);
  if (!hasOnlyAllowedKeys(payload, allowedKeys)) {
    return false;
  }

  return Object.entries(validatorsByField).every(([key, validator]) =>
    validator(payload[key])
  );
};

export const isPortfolioTopPerformingProductsPayload = (payload) =>
  validateDataToolPayloadByType(
    DataToolType.PORTFOLIO_TOP_PERFORMING_PRODUCTS,
    payload
  );

export const isPortfolioQueryPayload = (payload) =>
  validateDataToolPayloadByType(DataToolType.PORTFOLIO_QUERY, payload);

export const isPortfolioTransactionsQueryPayload = (payload) =>
  validateDataToolPayloadByType(
    DataToolType.PORTFOLIO_TRANSACTIONS_QUERY,
    payload
  );

export const isPortfolioAccessibleAccountsPayload = (payload) =>
  validateDataToolPayloadByType(
    DataToolType.PORTFOLIO_ACCESSIBLE_ACCOUNTS,
    payload
  );

export const isPortfolioMarketvaluesQueryPayload = (payload) =>
  validateDataToolPayloadByType(
    DataToolType.PORTFOLIO_MARKETVALUES_QUERY,
    payload
  );

export const isPortfolioSecuritiesQueryPayload = (payload) =>
  validateDataToolPayloadByType(
    DataToolType.PORTFOLIO_SECURITIES_QUERY,
    payload
  );

export const isPortfolioFundsQueryPayload = (payload) =>
  validateDataToolPayloadByType(DataToolType.PORTFOLIO_FUNDS_QUERY, payload);

export const isPortfolioCostReportQueryPayload = (payload) =>
  validateDataToolPayloadByType(
    DataToolType.PORTFOLIO_COST_REPORT_QUERY,
    payload
  );

export const isPortfolioStrategyQueryPayload = (payload) =>
  validateDataToolPayloadByType(DataToolType.PORTFOLIO_STRATEGY_QUERY, payload);

export const isPortfolioTaxDetailsQueryPayload = (payload) =>
  validateDataToolPayloadByType(
    DataToolType.PORTFOLIO_TAX_DETAILS_QUERY,
    payload
  );

export const isWealthPlanQueryPayload = (payload) =>
  validateDataToolPayloadByType(DataToolType.WEALTH_PLAN_QUERY, payload);

const dataToolValidators = Object.freeze(
  Object.values(DataToolType).reduce((validatorsMap, type) => {
    validatorsMap[type] = (payload) =>
      validateDataToolPayloadByType(type, payload);
    return validatorsMap;
  }, {})
);

export const isDataToolRequest = (tool) => {
  if (
    !isStrictObjectShape(tool, {
      requiredKeys: ['type', 'payload'],
      optionalKeys: [],
    }) ||
    !isDataToolType(tool.type)
  ) {
    return false;
  }

  const validator = dataToolValidators[tool.type];
  if (!validator) {
    return false;
  }

  return validator(tool.payload);
};

export const validateDataToolRequest = (tool) => {
  if (!isObject(tool)) {
    return createActionResultError(
      ACTION_ERROR_CODES.INVALID_ACTION,
      'Data tool shape/type is invalid.',
      tool
    );
  }

  if (!isDataToolType(tool.type)) {
    return createActionResultError(
      ACTION_ERROR_CODES.UNKNOWN_ACTION,
      `Unknown data tool type: ${tool.type || 'undefined'}.`,
      tool
    );
  }

  if (!isDataToolRequest(tool)) {
    return createActionResultError(
      ACTION_ERROR_CODES.INVALID_PAYLOAD,
      'Data tool payload is invalid.',
      tool
    );
  }

  return createActionResultOk(tool);
};

export const isDataToolResult = (toolResult) => {
  if (
    !isStrictObjectShape(toolResult, {
      requiredKeys: ['tool', 'status'],
      optionalKeys: ['result', 'error'],
    })
  ) {
    return false;
  }

  if (!isDataToolRequest(toolResult.tool)) {
    return false;
  }

  if (toolResult.status !== 'ok' && toolResult.status !== 'error') {
    return false;
  }

  if (toolResult.status === 'error') {
    return isActionError(toolResult.error);
  }

  return true;
};

const validators = {
  [UIActionType.NAVIGATE_TARGET]: isNavigateTargetPayload,
  [UIActionType.NAVIGATE_ROUTE]: isNavigateRoutePayload,
  [UIActionType.NAVIGATE_SCREEN]: isNavigateScreenPayload,
  [UIActionType.PORTFOLIO_SET_DATE_RANGE]: isPortfolioSetDateRangePayload,
  [UIActionType.PORTFOLIO_FILTER_PERIOD]: isPortfolioFilterPeriodPayload,
  [UIActionType.PORTFOLIO_SET_FILTERS]: isPortfolioSetFiltersPayload,
  [UIActionType.PORTFOLIO_FILTER_SSIDS]: isPortfolioFilterSsidsPayload,
  [UIActionType.PORTFOLIO_FILTER_EXTERNAL_REPORTING]:
    isPortfolioFilterExternalReportingPayload,
  [UIActionType.PORTFOLIO_FILTER_TAX_CLASS]: isPortfolioFilterTaxClassPayload,
  [UIActionType.PORTFOLIO_FILTER_INVESTMENT_ACCOUNT]:
    isPortfolioFilterInvestmentAccountPayload,
  [UIActionType.PORTFOLIO_ACTIVE_STRATEGY]: isPortfolioActiveStrategyPayload,
  [UIActionType.FEED_FILTER]: isFeedFilterPayload,
  [UIActionType.DOCUMENTS_FILTER_RELATIONSHIPS]:
    isDocumentsFilterRelationshipsPayload,
  [UIActionType.DOCUMENTS_FILTER_YEARS]: isDocumentsFilterYearsPayload,
  [UIActionType.DOCUMENTS_FILTER_TYPES]: isDocumentsFilterTypesPayload,
  [UIActionType.DOCUMENTS_RESET_FILTERS]: isDocumentsResetFiltersPayload,
  [UIActionType.DOCUMENTS_SHOW_ONLY_UNREAD]: isDocumentsShowOnlyUnreadPayload,
  [UIActionType.USER_SET_LANGUAGE]: isUserSetLanguagePayload,
};

export const isUIAction = (action) => {
  if (
    !isStrictObjectShape(action, {
      requiredKeys: ['type', 'payload'],
      optionalKeys: [],
    }) ||
    !isUIActionType(action.type)
  ) {
    return false;
  }

  const validator = validators[action.type];
  if (!validator) {
    return false;
  }

  return validator(action.payload);
};

export const validateUIAction = (action) => {
  if (!isObject(action)) {
    return createActionResultError(
      ACTION_ERROR_CODES.INVALID_ACTION,
      'Action shape/type is invalid.',
      action
    );
  }

  if (!isUIActionType(action.type)) {
    return createActionResultError(
      ACTION_ERROR_CODES.UNKNOWN_ACTION,
      `Unknown action type: ${action.type || 'undefined'}.`,
      action
    );
  }

  if (!isUIAction(action)) {
    return createActionResultError(
      ACTION_ERROR_CODES.INVALID_PAYLOAD,
      'Action payload is invalid.',
      action
    );
  }

  return createActionResultOk(action);
};

export const isUIActionEnvelope = (envelope) => {
  if (!isObject(envelope)) return false;

  if (typeof envelope.contractVersion !== 'string') return false;

  if (!Array.isArray(envelope.actions)) return false;

  return envelope.actions.every(isUIAction);
};

const isClientAIAccount = (value) =>
  isStrictObjectShape(value, {
    requiredKeys: ['name', 'ssid'],
    optionalKeys: [],
  }) &&
  isNonEmptyString(value.name) &&
  isNonEmptyString(value.ssid);

const isOptionalObject = (value) =>
  value === undefined || value === null || isObject(value);

const isOptionalClientAIAccountList = (value) =>
  value === undefined ||
  (Array.isArray(value) && value.every(isClientAIAccount));

const isCurrentViewContext = (value) =>
  isStrictObjectShape(value, {
    requiredKeys: ['route'],
    optionalKeys: ['viewId', 'entityType'],
  }) &&
  isNonEmptyString(value.route) &&
  isOptionalString(value.viewId) &&
  isOptionalString(value.entityType);

const isPortfolioContext = (value) =>
  isStrictObjectShape(value, {
    requiredKeys: [],
    optionalKeys: [
      'activeFilters',
      'selectedSsids',
      'selectedAccounts',
      'accessibleAccounts',
    ],
  }) &&
  isOptionalObject(value.activeFilters) &&
  (value.selectedSsids === undefined ||
    isArrayOfStrings(value.selectedSsids)) &&
  isOptionalClientAIAccountList(value.selectedAccounts) &&
  isOptionalClientAIAccountList(value.accessibleAccounts);

const isReportingContext = (value) =>
  isStrictObjectShape(value, {
    requiredKeys: [],
    optionalKeys: ['reportId', 'activeFilters'],
  }) &&
  isOptionalString(value.reportId) &&
  isOptionalObject(value.activeFilters);

const isWealthPlanContext = (value) =>
  isStrictObjectShape(value, {
    requiredKeys: [],
    optionalKeys: ['section', 'activeFilters'],
  }) &&
  isOptionalString(value.section) &&
  isOptionalObject(value.activeFilters);

const isUICapabilitiesContext = (value) =>
  isStrictObjectShape(value, {
    requiredKeys: ['canNavigate', 'canApplyFilters'],
    optionalKeys: [],
  }) &&
  typeof value.canNavigate === 'boolean' &&
  typeof value.canApplyFilters === 'boolean';

export const isClientAIContext = (value) =>
  isStrictObjectShape(value, {
    requiredKeys: [
      'platform',
      'locale',
      'timezone',
      'currentView',
      'uiCapabilities',
    ],
    optionalKeys: ['portfolio', 'reporting', 'wealthPlan'],
  }) &&
  (value.platform === 'web' || value.platform === 'mobile') &&
  isNonEmptyString(value.locale) &&
  isNonEmptyString(value.timezone) &&
  isCurrentViewContext(value.currentView) &&
  isUICapabilitiesContext(value.uiCapabilities) &&
  (value.portfolio === undefined || isPortfolioContext(value.portfolio)) &&
  (value.reporting === undefined || isReportingContext(value.reporting)) &&
  (value.wealthPlan === undefined || isWealthPlanContext(value.wealthPlan));

export const validateClientAIContext = (context) => {
  if (!isClientAIContext(context)) {
    return createActionResultError(
      ACTION_ERROR_CODES.INVALID_PAYLOAD,
      'Client AI context shape is invalid.',
      context
    );
  }

  return createActionResultOk(context);
};

export const isAISendThreadMessageRequest = (request) => {
  if (!isObject(request)) return false;

  return (
    typeof request.message === 'string' &&
    request.message.trim().length > 0 &&
    isClientAIContext(request.context)
  );
};

export const isAIMessageFeedbackRequest = (request) =>
  isStrictObjectShape(request, {
    requiredKeys: ['rating'],
    optionalKeys: [],
  }) && request.rating === 'bad';

export const isAIChatRequest = isAISendThreadMessageRequest;

export const isAIThread = (value) =>
  isObject(value) &&
  isStringOrNumber(value.id) &&
  isOptionalString(value.title) &&
  isOptionalString(value.preview) &&
  typeof value.message_count === 'number' &&
  isOptionalString(value.created_at) &&
  isOptionalString(value.updated_at) &&
  isOptionalString(value.last_message_at);

export const isAssistantTurn = (value) =>
  isStrictObjectShape(value, {
    requiredKeys: ['reply', 'ui_actions', 'tool_results'],
    optionalKeys: [],
  }) &&
  typeof value.reply === 'string' &&
  Array.isArray(value.ui_actions) &&
  value.ui_actions.every(isUIAction) &&
  Array.isArray(value.tool_results) &&
  value.tool_results.every(isDataToolResult);

export const isAIThreadMessage = (value) => {
  if (
    !isStrictObjectShape(value, {
      requiredKeys: ['id', 'thread_id', 'role', 'content'],
      optionalKeys: ['created_at', 'ui_actions', 'tool_results'],
    }) ||
    !isStringOrNumber(value.id) ||
    !isStringOrNumber(value.thread_id) ||
    (value.role !== 'user' && value.role !== 'assistant') ||
    typeof value.content !== 'string' ||
    isOptionalString(value.created_at) === false
  ) {
    return false;
  }

  if (value.ui_actions !== undefined && !Array.isArray(value.ui_actions)) {
    return false;
  }

  if (value.ui_actions !== undefined && !value.ui_actions.every(isUIAction)) {
    return false;
  }

  if (value.tool_results !== undefined && !Array.isArray(value.tool_results)) {
    return false;
  }

  if (
    value.tool_results !== undefined &&
    !value.tool_results.every(isDataToolResult)
  ) {
    return false;
  }

  return true;
};

export const isAIThreadListResponse = (response) =>
  isObject(response) &&
  Array.isArray(response.threads) &&
  response.threads.every(isAIThread);

export const isAIThreadResponse = (response) =>
  isObject(response) && isAIThread(response.thread);

export const isAIThreadMessagesResponse = (response) =>
  isObject(response) &&
  isAIThread(response.thread) &&
  Array.isArray(response.messages) &&
  response.messages.every(isAIThreadMessage);

export const isAISendThreadMessageResponse = (response) =>
  isObject(response) &&
  isAIThread(response.thread) &&
  (response.user_message === undefined ||
    response.user_message === null ||
    isAIThreadMessage(response.user_message)) &&
  (response.assistant_message === undefined ||
    response.assistant_message === null ||
    isAIThreadMessage(response.assistant_message)) &&
  typeof response.reply === 'string' &&
  Array.isArray(response.ui_actions) &&
  response.ui_actions.every(isUIAction) &&
  Array.isArray(response.tool_results) &&
  response.tool_results.every(isDataToolResult);

export const isAIChatResponse = (response) => isAssistantTurn(response);
